/**
 * Which verdict line and recommendation the AI Access Checker shows, as
 * translation keys plus values. Pure, so the branching is testable without a
 * renderer; the component turns each line into text with `t`.
 *
 * Three roles, in consequence order: AI Visitors (live answers, red), AI
 * Search Indexers (an assistant's own search index, yellow and informational)
 * and AI Model Trainers (future models, yellow). An indexer block never makes
 * the verdict red, and the copy always leaves room for the site still being
 * cited, because in our citation data many index-blocked sites still are.
 */

import { resolveAiPlatformLabel } from "@repo/core/client";
import type { AiAccessCheckResponse, BlockedBotInfo } from "./api";

/** Keys are relative to `marketing.aiAccessChecker`. */
export type CopyKey =
	| "summary.unreachable"
	| "summary.missing"
	| "summary.visitors"
	| "summary.visitorsAlsoSearch"
	| "summary.indexers"
	| "summary.trainers"
	| "summary.open"
	| "results.redRecommendation"
	| "results.indexerRecommendation"
	| "results.yellowRecommendation";

export interface CopyLine {
	key: CopyKey;
	values?: Record<string, string | number>;
}

/** `unknown`: robots.txt couldn't be loaded, so there is no verdict. */
export type AccessTone = "green" | "yellow" | "red" | "unknown";

export type AiBotGroupId =
	| "aiVisitors"
	| "aiSearchIndexers"
	| "aiModelTrainers";

/** The three groups in display order, with the colour a block earns. */
export const AI_BOT_GROUPS: ReadonlyArray<{
	id: AiBotGroupId;
	blockedTone: "red" | "yellow";
}> = [
	{ id: "aiVisitors", blockedTone: "red" },
	{ id: "aiSearchIndexers", blockedTone: "yellow" },
	{ id: "aiModelTrainers", blockedTone: "yellow" },
];

/** How lists and possessives are written in the reader's language. */
export interface CopyFormat {
	/** "A, B and C" in the current locale. */
	list: (names: string[]) => string;
	/** "OpenAI's" in English; the bare name where the sentence handles it. */
	possessive: (name: string) => string;
}

type Blocked = Record<AiBotGroupId, BlockedBotInfo[]>;

/**
 * The blocked bots per group. `aiSearchIndexers` is defaulted because cached
 * results and API callers built before AI search indexers existed lack it.
 */
export function blockedByGroup(result: AiAccessCheckResponse): Blocked {
	const { blockedBots } = result.analysis;
	return {
		aiVisitors: blockedBots.aiVisitors ?? [],
		aiSearchIndexers: blockedBots.aiSearchIndexers ?? [],
		aiModelTrainers: blockedBots.aiModelTrainers ?? [],
	};
}

export function isCloudflareBlocked(result: AiAccessCheckResponse): boolean {
	// The explicit flag may not survive Hono RPC type inference, so the
	// summary text is a fallback.
	return (
		result.robotsTxtState === "cloudflare_blocked" ||
		result.cloudflareBlocked === true ||
		result.analysis.summary.toLowerCase().includes("cloudflare")
	);
}

/** True when robots.txt couldn't be loaded and no verdict is possible. */
export function isUnreachable(result: AiAccessCheckResponse): boolean {
	return (
		result.robotsTxtState === "unreachable" && !isCloudflareBlocked(result)
	);
}

export function accessTone(result: AiAccessCheckResponse): AccessTone {
	return isUnreachable(result) ? "unknown" : result.analysis.status;
}

function unique(values: string[]): string[] {
	return [...new Set(values.filter(Boolean))];
}

/** The assistants a set of bots serve: "ChatGPT" for ChatGPT-User. */
export function assistantsOf(bots: readonly BlockedBotInfo[]): string[] {
	return unique(
		bots.map((bot) =>
			resolveAiPlatformLabel({ botName: bot.name, company: bot.company }),
		),
	);
}

/**
 * The one-line verdict under the domain. Null for a Cloudflare block, which
 * keeps the server's own explanation. Never claims more certainty than the
 * robots.txt rules support.
 */
export function accessSummaryLines(
	result: AiAccessCheckResponse,
	fmt: CopyFormat,
): CopyLine[] | null {
	if (isCloudflareBlocked(result)) return null;
	if (isUnreachable(result)) return [{ key: "summary.unreachable" }];
	if (result.robotsTxtState === "missing" || !result.robotsTxtFound) {
		return [{ key: "summary.missing" }];
	}

	const { aiVisitors, aiSearchIndexers, aiModelTrainers } =
		blockedByGroup(result);
	const searchAssistants = assistantsOf(aiSearchIndexers);

	if (aiVisitors.length > 0) {
		const live = assistantsOf(aiVisitors);
		const lines: CopyLine[] = [
			{
				key: "summary.visitors",
				values: { assistants: fmt.list(live), count: live.length },
			},
		];
		if (searchAssistants.length > 0) {
			lines.push({
				key: "summary.visitorsAlsoSearch",
				values: { assistants: fmt.list(searchAssistants) },
			});
		}
		return lines;
	}
	if (aiSearchIndexers.length > 0) {
		const owners = unique(
			aiSearchIndexers.map((bot) => bot.company || bot.name),
		);
		return [
			{
				key: "summary.indexers",
				values: {
					owners: fmt.list(owners.map(fmt.possessive)),
					count: aiSearchIndexers.length,
					assistants: fmt.list(searchAssistants),
				},
			},
		];
	}
	if (aiModelTrainers.length > 0) {
		return [{ key: "summary.trainers" }];
	}
	return [{ key: "summary.open" }];
}

/** The recommendation panel under the results card (yellow and red only). */
export function accessRecommendationLine(
	result: AiAccessCheckResponse,
	fmt: CopyFormat,
): CopyLine | null {
	if (isCloudflareBlocked(result) || isUnreachable(result)) return null;
	const { aiVisitors, aiSearchIndexers } = blockedByGroup(result);
	const status = result.analysis.status;
	if (status === "red") return { key: "results.redRecommendation" };
	if (status !== "yellow") return null;
	if (aiVisitors.length === 0 && aiSearchIndexers.length > 0) {
		return {
			key: "results.indexerRecommendation",
			values: {
				assistants: fmt.list(assistantsOf(aiSearchIndexers)),
				names: fmt.list(
					unique(aiSearchIndexers.map((bot) => bot.name)),
				),
			},
		};
	}
	return { key: "results.yellowRecommendation" };
}
